<script lang="ts">
import type { FeaturedCity } from '@consumer/types/serializers'

/**
 * A city that can actually be acted on. `FeaturedCity.slug` is optional on the
 * serializer, but CitySelect never emits a row without one — nothing downstream
 * could address it — so consumers get the slug guaranteed.
 */
export type SelectableCity = FeaturedCity & { slug: string }
</script>

<script setup lang="ts">
/**
 * A city picker: a trigger showing the current city, opening a searchable list
 * of featured cities. Desktop popover, mobile bottom sheet.
 *
 * Knows nothing about what a selection is *for* — it emits the chosen city and
 * the panel's `close`, so the consumer can finish its own work (navigate, swap
 * a carousel, save a preference) before dismissing the panel.
 */
import GSheetPopover from '@consumer/components/GSheetPopover.vue'
import GIcon from '@consumer/components/GIcon.vue'
import CityImage from '@consumer/components/CityImage.vue'
import V2List from '@consumer/components/V2/V2List.vue'
import V2ListItem from '@consumer/components/V2/V2ListItem.vue'
import AutocompleteApi from '@consumer/api/AutocompleteApi'
import { typeaheadDebounce } from '@shared/debounce'
import { faChevronDown, faLocationArrow, faLocationDot } from '@fortawesome/pro-light-svg-icons'
import everywhereIcon from '@consumer/assets/icons/everywhere.svg?svgicon'

withDefaults(defineProps<{
  // The current city. Null shows `fallbackLabel` on the trigger instead.
  selectedCity?: FeaturedCity | null
  fallbackLabel: string
  // Rows offered before anything is typed — the caller decides the ranking.
  cities?: FeaturedCity[]
  title?: string
  searchPlaceholder?: string
  emptyText?: string
  placement?: 'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top-end' | 'top'
  panelClass?: string
  maxPanelHeight?: number
  // Set while the consumer is acting on a selection, to block a second pick.
  busy?: boolean
  // Shown inside the panel, so a failure stays visible next to the list.
  error?: string | null
  gaTrackingString?: string
  testId?: string
  // Where typed queries are looked up. Defaults to every active featured city;
  // pass this to scope the search to a narrower set.
  searchCities?: (query: string) => Promise<FeaturedCity[]>
  // Opt-in row for a not-a-city choice, e.g. "Nationwide". Emits selectNationwide.
  nationwideLabel?: string
  // Opt-in row returning to the visitor's own location, e.g. "Near me". Only
  // offered once a city is selected — there is nothing to undo before that.
  resetLabel?: string
}>(), { selectedCity: null,cities: () => ([]),title: 'Choose a city',searchPlaceholder: 'Search for a city',emptyText: 'No cities match that search.',placement: 'bottom-start',panelClass: 'w-80',maxPanelHeight: 360,busy: false,error: null,testId: 'city-select', })

const emit = defineEmits<{
  select: [city: SelectableCity, close: () => void]
  selectNationwide: [close: () => void]
  reset: [close: () => void]
}>()

const query = ref('')
const matches = ref<FeaturedCity[]>([])

const label = computed(() => __props.selectedCity?.displayName ?? __props.fallbackLabel)
const rows = computed(() => (query.value.trim() ? matches.value : __props.cities))

// Pinned rows belong to the blank state only — offering "Nationwide" among the
// results for "atl" would be noise.
const searching = computed(() => Boolean(query.value.trim()))
const showNationwide = computed(() => !searching.value && Boolean(__props.nationwideLabel))
const showReset = computed(() => !searching.value && Boolean(__props.resetLabel) && Boolean(__props.selectedCity))
const hasRows = computed(() => rows.value.length > 0 || showNationwide.value || showReset.value)

function lookup (term: string) {
  if (__props.searchCities) return __props.searchCities(term)

  return AutocompleteApi.featuredCities<FeaturedCity[]>({ query: { q: term } })
}

const search = typeaheadDebounce(async (term: string) => {
  if (!term.trim()) {
    matches.value = []
    return
  }
  matches.value = await lookup(term)
})

watch(query, term => search(term))

function rowImage (city: FeaturedCity) {
  return city.imageThumbUrl || undefined
}

// A row without a slug isn't addressable, so it never reaches the consumer —
// which is what lets the emit promise one.
function selectCity (city: FeaturedCity, close: () => void) {
  if (__props.busy || !city.slug) return

  emit('select', city as SelectableCity, close)
}

function selectNationwide (close: () => void) {
  if (__props.busy) return

  emit('selectNationwide', close)
}

function reset (close: () => void) {
  if (__props.busy) return

  emit('reset', close)
}

// headlessui focuses the trigger when the panel opens and restores focus when
// it closes. The browser can't attribute that programmatic focus to a pointer,
// so :focus-visible matches and a mouse click leaves a ring behind that outlasts
// the interaction. Track the modality ourselves and let the CSS opt out.
//
// Only blur clears it: the trigger keeps focus for the whole open/close cycle,
// so clearing on keydown would bring the ring back the moment Escape closes a
// panel the pointer opened. Tabbing away blurs, so a later keyboard focus still
// gets its ring.
const pointerFocus = ref(false)

// Everything the trigger needs to function, as one bag a #triggerButton
// override can spread. Forget it and the trigger renders but does nothing, so
// keep this the single thing a caller has to remember.
function triggerProps (open: unknown) {
  return {
    type: 'button' as const,
    'data-track-ga': __props.gaTrackingString,
    'data-pointer-focus': pointerFocus.value || undefined,
    onClick: () => openPanel(open),
    onPointerdown: () => { pointerFocus.value = true },
    onBlur: () => { pointerFocus.value = false },
  }
}

// GSheetPopover hands #button an open STATE (boolean) on desktop — where its
// PopoverButton already handles the click — and an open ACTION (function) on
// mobile's bottom sheet. Only invoke it when it's the action.
function openPanel (open: unknown) {
  if (typeof open === 'function') open()
}
</script>

<template>
  <GSheetPopover
    :placement="placement"
    :panelClass="panelClass"
    :maxPanelHeight="maxPanelHeight"
    noPadding
  >
    <template #header>
      {{ title }}
    </template>

    <!-- Whatever fills this must be a SINGLE root with no sibling nodes (not
         even a comment): on desktop PopoverButton renders as="template" and
         forwards its handlers onto that one root, so a sibling makes it a
         fragment and headlessui throws "Passing props on template". v-if/v-else
         rather than a slot fallback, so exactly one vnode reaches it either way.

         Two levels of override: #trigger replaces the button's *contents*, and
         #triggerButton replaces the *element* — for a different layout, or to
         render an <a> without nesting it inside our button. -->
    <template #button="{ open, isOpen }">
      <slot
        v-if="$slots.triggerButton"
        name="triggerButton"
        :triggerProps="triggerProps(open)"
        :label="label"
        :icon="faChevronDown"
        :isOpen="isOpen"
      />
      <button
        v-else
        class="city-select-trigger flex items-center gap-1 text-md font-semibold text-v2-active clickable"
        v-bind="triggerProps(open)"
      >
        <!-- Overrides get `isOpen` so they can reflect the open state too — it's
             a plain boolean on both branches, unlike `open`, which is the open
             *action* on mobile rather than the state. -->
        <slot name="trigger" :label="label" :icon="faChevronDown" :isOpen="isOpen">
          <span class="city-select-label">{{ label }}</span>
          <!-- Rotated from this component's own stylesheet, not a rotate-*
               utility: app/frontend/consumer is excluded from UnoCSS
               (vite.config.ts) and served by WindiCSS, which emits no
               `rotate-180` here — verified with a static class, so it isn't
               about the :class binding. A scoped rule also travels with the
               component if it's ever used from a differently-scanned tree. -->
          <GIcon
            :icon="faChevronDown"
            class="city-select-chevron"
            :class="{ 'is-open': isOpen }"
          />
        </slot>
      </button>
    </template>

    <template #default="{ close }">
      <div class="flex flex-col min-h-0">
        <div class="p-3 border-b-1 border-grey-primary">
          <input
            v-model="query"
            type="search"
            class="city-select-search w-full text-sm p-2 rounded-v2sm border-1 border-grey-primary"
            :placeholder="searchPlaceholder"
            :aria-label="searchPlaceholder"
            :data-testid="`${testId}-search`"
          >
        </div>

        <!-- aria-busy so the dimming isn't the only signal that the list is
             mid-action; it is rendered as "false" rather than dropped, because
             aria-* attributes are not treated as boolean attributes. -->
        <div
          class="min-h-0 overflow-y-auto p-2"
          :class="busy ? 'opacity-60 pointer-events-none' : ''"
          :aria-busy="busy"
          :data-testid="`${testId}-options`"
        >
          <V2List
            v-if="hasRows"
            class="w-full !border-0 !shadow-none"
            :data-testid="`${testId}-list`"
          >
            <!-- Pinned above the cities: the escape hatch first, then the
                 not-a-city choice, matching multiPlaceGiftIdea's picker. -->
            <V2ListItem
              v-if="showReset"
              as="button"
              class="w-full cursor-pointer"
              :label="resetLabel"
              :icon="faLocationArrow"
              :data-testid="`${testId}-reset`"
              @click="reset(close)"
            />
            <V2ListItem
              v-if="showNationwide"
              as="button"
              class="w-full cursor-pointer"
              :label="nationwideLabel"
              :icon="everywhereIcon"
              :data-testid="`${testId}-nationwide`"
              @click="selectNationwide(close)"
            />
            <V2ListItem
              v-for="city in rows"
              :key="city.slug ?? city.displayName"
              as="button"
              class="w-full cursor-pointer"
              :label="city.displayName"
              :image="rowImage(city)"
              :icon="rowImage(city) ? undefined : faLocationDot"
              loading="lazy"
              :data-testid="`${testId}-option`"
              @click="selectCity(city, close)"
            >
              <template #image="{ image }">
                <CityImage :imageUrl="image" size="small"/>
              </template>
            </V2ListItem>
          </V2List>

          <p v-else class="text-sm text-v2-tertiary p-2">
            {{ emptyText }}
          </p>
        </div>

        <p v-if="error" class="text-sm text-v2-error px-3 pb-3" role="alert">
          {{ error }}
        </p>
      </div>
    </template>
  </GSheetPopover>
</template>

<style lang="scss" scoped>
// WCAG 2.2 Target Size: 24x24 is the AA floor, 44x44 the AAA / touch-platform
// figure, and what GButton already uses for its mobile controls. The trigger's
// text box is only 22px tall, so the target is grown with an overlay rather
// than padding — padding grows the header row, which is what produced the
// early ellipsis that shrinking the label was meant to cure.
$min-target: 44px;

.city-select-trigger {
  // Lets it shrink when the row it sits in runs out of room — but never past
  // the target size. Still below the content width, so the label can go on
  // truncating on mobile. A max-width here would clip against a shrink-to-fit
  // parent and eat the label's width even when there's space to spare.
  min-width: $min-target;
  position: relative;
}

// Grows the hit area vertically only. `inset-inline: 0` holds it to the
// button's own width so it can't swallow clicks meant for the section title
// link, which sits one 8px gap to its left.
.city-select-trigger::after {
  content: "";
  height: $min-target;
  inset-inline: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

// A ring for keyboard users, nothing for mouse users. Same treatment as
// DropDownActions, the other dropdown trigger in this directory.
.city-select-trigger,
.city-select-search {
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-active);
    outline-offset: 2px;
  }
}

// Focus that arrived by pointer, however the browser classified it.
.city-select-trigger[data-pointer-focus]:focus-visible {
  outline: none;
}

// The rows come from V2ListItem, so reach into them rather than change focus
// behaviour for every other caller of that component.
:deep(.list-item button) {
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-active);
    outline-offset: -2px;
  }
}

.city-select-label {
  white-space: nowrap;
}

@media (max-width: 1023px) {
  .city-select-trigger {
    max-width: 100%;
  }

  .city-select-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.city-select-chevron {
  // Stays put while the label gives up width.
  flex-shrink: 0;
  transition: transform 200ms ease;

  &.is-open {
    transform: rotate(180deg);
  }
}
</style>
